/**
 * Commissions — Admin ledger page at /commissions (admin only).
 *
 * Tab 1 — Statements: bi-weekly period preview, Generate, then per-statement
 *          lifecycle actions (PDF · Send to rep · Mark Paid) + W2 CSV export.
 * Tab 2 — Rates: per-user commission rate editor (append-only rate history).
 */
import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import {
  DollarSign, FileText, Send, CheckCircle2, Download, Pencil,
  Loader2, RefreshCw, Copy, Percent,
} from "lucide-react";

const money = (v) => Number(v || 0).toLocaleString(undefined, { style: "currency", currency: "USD" });

const STMT_STATUS = {
  draft:       "bg-zinc-100 text-zinc-800",
  sent_to_rep: "bg-blue-100 text-blue-800",
  signed:      "bg-emerald-100 text-emerald-800",
  paid:        "bg-emerald-600 text-white",
};

async function openAuthedPdf(url, downloadAs = null) {
  const token = localStorage.getItem("crm_token");
  try {
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) throw new Error(`Failed (${r.status})`);
    const blob = await r.blob();
    const objectUrl = URL.createObjectURL(blob);
    if (downloadAs) {
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = downloadAs;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      window.open(objectUrl, "_blank");
    }
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch (e) {
    toast.error(e.message || "Could not open file");
  }
}

const BTN = "inline-flex items-center gap-1.5 h-8 px-2.5 text-[10px] font-bold uppercase tracking-wider rounded-sm transition-colors";

export default function Commissions() {
  const [tab, setTab] = useState("statements");
  return (
    <div className="p-6 sm:p-8 animate-in fade-in duration-500" data-testid="commissions-admin-page">
      <div className="flex items-end justify-between mb-6 pb-6 border-b border-zinc-200 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700 mb-2">Finance</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Commissions</h1>
          <div className="text-sm text-zinc-600 mt-1">Bi-weekly statements · every other Friday, one week behind</div>
        </div>
        <div className="inline-flex border border-zinc-300 rounded-sm overflow-hidden">
          {[["statements", "Statements"], ["rates", "Rates"]].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`commissions-tab-${k}`}
              className={`h-10 px-5 text-xs font-bold uppercase tracking-wider transition-colors ${tab === k ? "bg-zinc-950 text-white" : "bg-white text-zinc-700 hover:bg-zinc-100"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {tab === "statements" ? <StatementsTab /> : <RatesTab />}
    </div>
  );
}

// ─── Statements tab ─────────────────────────────────────────────────────────

function StatementsTab() {
  const [period, setPeriod] = useState(null);
  const [genDate, setGenDate] = useState("");
  const [statements, setStatements] = useState([]);
  const [openTotal, setOpenTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const [st, acc] = await Promise.all([
        api.get("/commissions/statements"),
        api.get("/commissions/accruals?status=open"),
      ]);
      setStatements(st.data || []);
      const open = (acc.data || []).filter((a) => !a.statement_id && a.recipient_type !== "subcontractor");
      setOpenTotal({
        total: open.reduce((s, a) => s + Number(a.commission_amount || 0), 0),
        count: open.length,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load statements");
    } finally {
      setLoading(false);
    }
  };

  const loadPeriod = (d) =>
    api.get(`/commissions/period-preview${d ? `?generation_date=${d}` : ""}`)
      .then((r) => setPeriod(r.data))
      .catch(() => {});

  useEffect(() => { load(); loadPeriod(""); }, []);
  useEffect(() => { loadPeriod(genDate); }, [genDate]);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/commissions/statements/generate", genDate ? { generation_date: genDate } : {});
      const n = r.data?.generated || 0;
      if (n === 0) toast.info("No open accruals in this period — nothing to generate.");
      else toast.success(`Generated ${n} statement${n === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const sendStatement = async (s) => {
    setBusyId(s.id);
    try {
      const r = await api.post(`/commissions/statements/${s.id}/send`, {});
      if (r.data?.email_sent) toast.success(`Statement emailed to ${r.data.sent_to}`);
      else toast.warning("Email delivery failed — sign link copied below. Send it manually.");
      if (r.data?.sign_url) {
        try { await navigator.clipboard.writeText(r.data.sign_url); toast.info("Sign link copied to clipboard"); } catch { /* noop */ }
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setBusyId(null);
    }
  };

  const markPaid = async (s) => {
    setBusyId(s.id);
    try {
      const r = await api.post(`/commissions/statements/${s.id}/mark-paid`, {});
      toast.success(
        r.data?.vendor_bill_id
          ? "Marked paid — 1099 vendor bill drafted in Payables"
          : `Marked paid via ${r.data?.method || "manual"}`
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mark-paid failed");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading statements…</div>;
  }

  return (
    <div className="space-y-6">
      {/* Generate banner */}
      <div className="bg-white border border-zinc-200 rounded-sm p-4 flex items-end gap-4 flex-wrap" data-testid="commissions-generate-card">
        <div className="flex-1 min-w-[240px]">
          <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1">Next Statement Run</div>
          <div className="text-sm text-zinc-800">
            Covers <b>{period?.period_start}</b> → <b>{period?.period_end}</b>
            <span className="text-zinc-500"> · generated {period?.generation_date}</span>
          </div>
          {openTotal && (
            <div className="text-xs text-zinc-500 mt-1" data-testid="commissions-open-accruals">
              Open rep accruals awaiting a statement: <b className="text-emerald-700">{money(openTotal.total)}</b> across {openTotal.count} row{openTotal.count === 1 ? "" : "s"}
            </div>
          )}
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1">Generation Date (back-fill)</label>
          <input
            type="date"
            value={genDate}
            onChange={(e) => setGenDate(e.target.value)}
            data-testid="commissions-gen-date"
            className="h-10 px-3 border border-zinc-300 rounded-sm text-sm"
          />
        </div>
        <button
          onClick={generate}
          disabled={generating}
          data-testid="commissions-generate-btn"
          className="inline-flex items-center gap-2 h-10 px-4 text-xs font-bold uppercase tracking-wider bg-emerald-700 hover:bg-emerald-800 text-white rounded-sm disabled:opacity-50"
        >
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Generate Statements
        </button>
        <button
          onClick={() => openAuthedPdf(`${API}/commissions/statements/export/w2-csv`, "sealtech-commissions-w2.csv")}
          data-testid="commissions-w2-export"
          className="inline-flex items-center gap-2 h-10 px-4 text-xs font-bold uppercase tracking-wider border border-zinc-300 text-zinc-800 hover:bg-zinc-100 rounded-sm"
          title="Signed W2 statements ready for payroll"
        >
          <Download className="w-4 h-4" /> W2 CSV
        </button>
      </div>

      {/* Statements table */}
      {statements.length === 0 ? (
        <div className="border border-dashed border-zinc-300 rounded-sm p-10 text-center text-sm text-zinc-500" data-testid="commissions-no-statements">
          No statements yet. Set rep rates in the <b>Rates</b> tab, collect an invoice payment, then hit Generate.
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-sm overflow-x-auto" data-testid="commissions-statements-table">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-4 py-2">Rep</th>
                <th className="text-left px-4 py-2">Period</th>
                <th className="text-right px-4 py-2">Total</th>
                <th className="text-center px-4 py-2">Payroll</th>
                <th className="text-center px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Signed</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {statements.map((s) => (
                <tr key={s.id} className="hover:bg-zinc-50" data-testid={`stmt-row-${s.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="font-bold text-zinc-950">{s.rep_name || "—"}</div>
                    <div className="text-[11px] text-zinc-500">{s.rep_email}</div>
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap text-zinc-700">{s.period_start} → {s.period_end}</td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums font-bold text-emerald-700">{money(s.total)}</td>
                  <td className="px-4 py-2.5 text-center text-xs text-zinc-600">{s.payroll_type || "1099"}</td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${STMT_STATUS[s.status] || "bg-zinc-100 text-zinc-800"}`}>
                      {(s.status || "").replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-zinc-600 whitespace-nowrap">
                    {s.signed_at ? `${s.signed_at.split("T")[0]} · ${s.signed_by_name || ""}` : "—"}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1.5 flex-wrap">
                      <button
                        onClick={() => openAuthedPdf(`${API}/commissions/statements/${s.id}/pdf`)}
                        data-testid={`stmt-pdf-${s.id}`}
                        className={`${BTN} border border-zinc-300 text-zinc-700 hover:bg-zinc-100`}
                      >
                        <FileText className="w-3.5 h-3.5" /> PDF
                      </button>
                      {s.status !== "paid" && (
                        <button
                          onClick={() => sendStatement(s)}
                          disabled={busyId === s.id}
                          data-testid={`stmt-send-${s.id}`}
                          className={`${BTN} border border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100 disabled:opacity-50`}
                        >
                          <Send className="w-3.5 h-3.5" /> {s.status === "draft" ? "Send to Rep" : "Resend"}
                        </button>
                      )}
                      {s.status === "signed" && (
                        <button
                          onClick={() => markPaid(s)}
                          disabled={busyId === s.id}
                          data-testid={`stmt-mark-paid-${s.id}`}
                          className={`${BTN} bg-emerald-700 hover:bg-emerald-800 text-white disabled:opacity-50`}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" /> Mark Paid
                        </button>
                      )}
                      {s.status === "paid" && s.vendor_bill_id && (
                        <span className="text-[10px] text-zinc-500">bill drafted</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Rates tab ──────────────────────────────────────────────────────────────

function RatesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // row being edited
  const [form, setForm] = useState({ rate_pct: "", payroll_type: "1099", effective_from: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/commissions/rates")
      .then((r) => setRows(r.data || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load rates"))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      rate_pct: row.rate_pct != null ? String(row.rate_pct) : "5",
      payroll_type: row.payroll_type || "1099",
      effective_from: "",
      notes: "",
    });
  };

  const save = async () => {
    const pct = parseFloat(form.rate_pct);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      toast.error("Rate must be between 0 and 100");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/commissions/rates/${editing.user_id}`, {
        rate_pct: pct,
        payroll_type: form.payroll_type,
        effective_from: form.effective_from || undefined,
        notes: form.notes,
      });
      toast.success(`Rate set to ${pct}% for ${editing.name}`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save rate");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading rates…</div>;
  }

  return (
    <div className="space-y-4">
      <div className="text-sm text-zinc-600">
        Commission = <b>rate % × rep&apos;s share of net profit</b> (contract − 20% overhead − job costs × 1.20).
        Users without a rate earn nothing. Rate history is append-only for audit.
      </div>
      <div className="bg-white border border-zinc-200 rounded-sm overflow-x-auto" data-testid="commissions-rates-table">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left px-4 py-2">User</th>
              <th className="text-center px-4 py-2">Role</th>
              <th className="text-right px-4 py-2">Current Rate</th>
              <th className="text-center px-4 py-2">Payroll</th>
              <th className="text-left px-4 py-2">Effective From</th>
              <th className="text-right px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {rows.map((r) => (
              <tr key={r.user_id} className="hover:bg-zinc-50" data-testid={`rate-row-${r.user_id}`}>
                <td className="px-4 py-2.5">
                  <div className="font-bold text-zinc-950">{r.name}</div>
                  <div className="text-[11px] text-zinc-500">{r.email}</div>
                </td>
                <td className="px-4 py-2.5 text-center text-xs text-zinc-600 uppercase">{r.role}</td>
                <td className="px-4 py-2.5 text-right">
                  {r.rate_pct != null ? (
                    <span className="font-mono tabular-nums font-bold text-emerald-700">{Number(r.rate_pct).toFixed(2)}%</span>
                  ) : (
                    <span className="text-[11px] text-zinc-400 italic">not on commission</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-center text-xs text-zinc-600">{r.rate_pct != null ? (r.payroll_type || "1099") : "—"}</td>
                <td className="px-4 py-2.5 text-xs text-zinc-600">{r.effective_from || "—"}</td>
                <td className="px-4 py-2.5 text-right">
                  <button
                    onClick={() => openEdit(r)}
                    data-testid={`rate-edit-${r.user_id}`}
                    className={`${BTN} border border-zinc-300 text-zinc-700 hover:bg-zinc-100`}
                  >
                    <Pencil className="w-3.5 h-3.5" /> {r.rate_pct != null ? "Change Rate" : "Set Rate"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" data-testid="rate-edit-modal">
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center gap-2">
              <Percent className="w-5 h-5 text-emerald-700" />
              <h2 className="font-heading text-lg font-bold tracking-tight">Commission Rate — {editing.name}</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1">Rate % of Net Profit</label>
                  <input
                    type="number" min="0" max="100" step="0.25"
                    value={form.rate_pct}
                    onChange={(e) => setForm((f) => ({ ...f, rate_pct: e.target.value }))}
                    data-testid="rate-input-pct"
                    className="w-full h-10 px-3 border border-zinc-300 rounded-sm text-sm"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1">Payroll Type</label>
                  <select
                    value={form.payroll_type}
                    onChange={(e) => setForm((f) => ({ ...f, payroll_type: e.target.value }))}
                    data-testid="rate-input-payroll"
                    className="w-full h-10 px-3 border border-zinc-300 rounded-sm text-sm"
                  >
                    <option value="1099">1099 — pays via Payables bill</option>
                    <option value="W2">W2 — exports to payroll CSV</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1">Effective From (blank = today)</label>
                <input
                  type="date"
                  value={form.effective_from}
                  onChange={(e) => setForm((f) => ({ ...f, effective_from: e.target.value }))}
                  data-testid="rate-input-effective"
                  className="w-full h-10 px-3 border border-zinc-300 rounded-sm text-sm"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1">Notes</label>
                <input
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="e.g. Promoted to senior rep"
                  data-testid="rate-input-notes"
                  className="w-full h-10 px-3 border border-zinc-300 rounded-sm text-sm"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
              <button
                onClick={() => setEditing(null)}
                data-testid="rate-cancel"
                className="h-10 px-4 text-xs font-bold uppercase tracking-wider border border-zinc-300 rounded-sm hover:bg-zinc-50"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                data-testid="rate-save"
                className="h-10 px-4 text-xs font-bold uppercase tracking-wider bg-emerald-700 hover:bg-emerald-800 text-white rounded-sm disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save Rate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
